import React, { useEffect, useState } from 'react';
import { Lock, Plus } from 'lucide-react';
import api from '../../services/api';
import { inizioScritto } from './TerapieInCorso';
import { medicoDellaVisita } from '../../utils/medicoDellaVisita';

/**
 * RiepilogoCartella — la prima linguetta della cartella (stile «Console»,
 * mockup «Paziente», Alex 6/10/2026): le visite e le procedure, le terapie
 * in corso, il pacchetto aperto, i prossimi appuntamenti, e sotto i dati
 * del paziente (`children`, che li compone PazienteDetail).
 *
 * QUI SI DISEGNA E BASTA. Ogni riquadro mostra cio' che una rotta ha gia'
 * deciso:
 *   - le visite arrivano dall'elenco che la cartella legge aprendosi
 *     (/visite/paziente/:id), gia' ridotte dal server per chi non vede una
 *     procedura riservata (riservata_nascosta, riservata_apribile): qui non
 *     si guarda chi le ha eseguite, si guarda il segno che il server mette;
 *   - le terapie sono le voci «farmaci» attive dell'anamnesi, la stessa
 *     lettura che alimenta l'avviso delle allergie; «Inizio» lo scrive
 *     inizioScritto, la stessa funzione della tabella dell'anamnesi; la nota
 *     di una terapia (migration 342) sotto il farmaco, in piccolo, com'e'
 *     scritta (gli a capo restano), come sotto la riga dell'anamnesi;
 *   - i pacchetti sono quelli che il server dice aperti (pacchetti_aperti,
 *     migration 299), nella forma ridotta per chi non li vede per intero;
 *   - i prossimi appuntamenti hanno giorno e ora gia' in ora italiana, e il
 *     colore e' quello dell'agenda (Alex, 2/10/2026: le categorie usano i
 *     colori che agende e stanze hanno gia' nei dati).
 *
 * LA LETTURA DEGLI APPUNTAMENTI E' L'UNICA CHE QUESTO RIQUADRO AGGIUNGE, e
 * lascia la sua traccia nel registro degli accessi come le altre letture per
 * paziente della cartella (agendaRoutes, getProssimi): la cartella e' fra le
 * pagine con traccia all'apertura (PAGINE_CON_TRACCIA), che non cambia. Se non
 * risponde lo dice nel riquadro: niente notifica, che resterebbe a video e
 * fermerebbe l'aggiornamento della pagina.
 *
 * E COSI' OGNI RIQUADRO (revisione del 6/10/2026): le visite e le terapie
 * arrivano come letture in tre stati (PazienteDetail: null, { elenco },
 * { errore: true }), e «Nessuna visita registrata.» o «Nessuna terapia in
 * corso.» si scrivono solo quando l'elenco e' arrivato ed e' vuoto. Una
 * lettura non riuscita lo dice, e dice dove guardare.
 */

/* «2026-09-28...» -> «28.09.2026», com'e' scritta nel dato, senza fuso. */
export const dataPunti = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/* Il titolo di una visita: le prestazioni, con la seduta del pacchetto
   («seduta 2 di 6») come la scrive la procedura. Una visita ridotta porta
   solo i nomi delle prestazioni, che chi non la apre puo' leggere. */
const titoloVisita = (v) => {
    const nomi = (v.prestazioni || [])
        .map((p) => {
            const nome = String(p.nome_snapshot || '').trim();
            if (!nome) return null;
            return p.seduta ? `${nome} · seduta ${p.seduta.numero} di ${p.seduta.di}` : nome;
        })
        .filter(Boolean);
    if (nomi.length) return nomi.join(' · ');
    if (v.riservata_nascosta) return v.patologia || 'Procedura riservata';
    return 'Visita senza prestazioni';
};

const medicoDi = (v) => medicoDellaVisita(v) || 'Medico non indicato';

/* Lo stato del referto di una visita, come lo manda la lettura delle visite
   (`referto_stato`, da referti_prodotti.stato). Un elenco chiuso: uno stato
   che qui non c'e' non si scrive, invece di mostrare una parola inventata. */
const STATO_REFERTO = {
    bozza: { testo: 'In bozza', segno: 'attenzione' },
    concluso: { testo: 'Conclusa', segno: 'neutro' },
    firmato: { testo: 'Firmata', segno: 'ok' },
    inviato_fse: { testo: 'Inviata al FSE', segno: 'info' },
};

/* Le visite del riquadro: le piu' recenti. */
const QUANTE_VISITE = 5;

const RiquadroVisite = ({ lettura, onApriVisita, onTutte }) => {
    const visite = lettura?.elenco || [];
    const ordinate = [...visite]
        .sort((a, b) => String(b.data_visita).localeCompare(String(a.data_visita)) || b.id - a.id)
        .slice(0, QUANTE_VISITE);
    return (
        <section className="crt-riquadro" aria-labelledby="crt-titolo-visite">
            <div className="crt-riquadro-testa">
                {/* Il nome della linguetta, come nel mockup (revisione del
                    6/10/2026: era «Visite e procedure mediche», il titolo
                    dell'elenco, scelto per far passare la rotta «procedura» di
                    verificaRotte, che cosi' trovava gia' qui il testo che
                    doveva comparire solo dopo il clic). */}
                <h2 id="crt-titolo-visite">Visite e procedure</h2>
                {visite.length > 0 && (
                    <button type="button" className="crt-link" onClick={onTutte}>
                        Tutte · <span className="rm-numeri">{visite.length}</span>
                    </button>
                )}
            </div>
            {!lettura ? (
                <p className="crt-vuoto">Caricamento…</p>
            ) : lettura.errore ? (
                <p className="crt-vuoto crt-non-letto" data-lettura="non-riuscita">
                    Non riesco a leggere le visite: le trovi in «Visite e procedure».
                </p>
            ) : ordinate.length === 0 ? (
                <p className="crt-vuoto">Nessuna visita registrata.</p>
            ) : (
                <ul className="crt-elenco">
                    {ordinate.map((v) => {
                        const nascosta = v.riservata_nascosta === true;
                        return (
                            <li key={v.id}>
                                <button type="button" className="crt-visita" data-visita={v.id}
                                        onClick={() => onApriVisita(v.id)}>
                                    <span className="crt-visita-data rm-numeri">{dataPunti(v.data_visita)}</span>
                                    <span className="crt-visita-testo">
                                        <span className="crt-visita-titolo rm-tronca" title={nascosta ? undefined : titoloVisita(v)}>
                                            {v.riservata && (
                                                <Lock size={14} strokeWidth={2} aria-label="Riservata" className="crt-lucchetto" />
                                            )}
                                            {nascosta ? 'Procedura riservata' : titoloVisita(v)}
                                        </span>
                                        <span className="crt-visita-chi rm-tronca"
                                              title={`${medicoDi(v)}${nascosta ? ` · ${titoloVisita(v)}` : ''}`}>
                                            {medicoDi(v)}
                                            {nascosta && ` · ${titoloVisita(v)}`}
                                        </span>
                                    </span>
                                    {nascosta ? (
                                        /* Il riquadro porta alla visita, dove il pannello della
                                           riservata offre le strade vere (chiedere
                                           l'autorizzazione, aprire per urgenza, o aprire da se'
                                           per chi puo'): qui non si apre niente. */
                                        <span className="crt-chiedi">
                                            {v.riservata_apribile ? 'Apri' : 'Chiedi accesso'}
                                        </span>
                                    ) : STATO_REFERTO[v.referto_stato] ? (
                                        /* Lo stato del referto (C3, Alex 13/10/2026): un campo
                                           in piu' nella lettura delle visite (referto_stato), il
                                           referto piu' recente della visita; la visita che non ne
                                           ha non scrive niente. */
                                        <span className={`crt-stato crt-stato--${STATO_REFERTO[v.referto_stato].segno}`}
                                              data-referto={v.referto_stato}>
                                            {STATO_REFERTO[v.referto_stato].testo}
                                        </span>
                                    ) : v.di_oggi ? (
                                        <span className="crt-stato crt-stato--oggi">Oggi</span>
                                    ) : (v.prestazioni || []).length === 0 ? (
                                        <span className="crt-stato">Senza prestazioni</span>
                                    ) : <span />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};

const RiquadroTerapie = ({ lettura, onAggiungi }) => {
    const terapie = (lettura?.elenco || []).filter((v) => v.sezione === 'farmaci' && v.stato === 'attiva');
    return (
        <section className="crt-riquadro" aria-labelledby="crt-titolo-terapie">
            <div className="crt-riquadro-testa">
                <h2 id="crt-titolo-terapie">Terapie in corso</h2>
                <button type="button" className="crt-btn crt-btn--piccolo" onClick={onAggiungi}>
                    <Plus size={13} strokeWidth={2} aria-hidden="true" /> Aggiungi
                </button>
            </div>
            {!lettura ? (
                <p className="crt-vuoto">Caricamento…</p>
            ) : lettura.errore ? (
                <p className="crt-vuoto crt-non-letto" data-lettura="non-riuscita">
                    Non riesco a leggere le terapie: le trovi nell'anamnesi, nel Profilo medico.
                </p>
            ) : terapie.length === 0 ? (
                <p className="crt-vuoto">Nessuna terapia in corso.</p>
            ) : (
                <div className="rm-scorre">
                    <table className="crt-tabella">
                        <thead>
                            <tr>
                                <th>Farmaco</th>
                                <th>Dosaggio</th>
                                <th className="crt-destra">Inizio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {terapie.map((v) => (
                                <tr key={v.id}>
                                    <td>
                                        {v.testo}
                                        {v.nota && <div className="crt-secondario crt-ter-nota">{v.nota}</div>}
                                    </td>
                                    <td className="crt-secondario">{v.dosaggio || '—'}</td>
                                    <td className="crt-destra rm-numeri">{inizioScritto(v) || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

/* «SCADE 09.2027»: il mese e l'anno, come nel mockup. */
const meseScadenza = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})/);
    return m ? `${m[2]}.${m[1]}` : null;
};

const RiquadroPacchetto = ({ pacchetto: p }) => {
    const n = Number(p.numero_sedute) || 0;
    const fatte = Math.min(Number(p.sedute_eseguite) || 0, n);
    const restano = Number(p.sedute_residue) || 0;
    const scade = meseScadenza(p.data_scadenza);
    return (
        <section className="crt-riquadro crt-pacchetto" data-pacchetto={p.id}>
            <div className="crt-pacchetto-testa">
                <h2>{p.descrizione}</h2>
                {scade && <span className="crt-scade rm-numeri">SCADE {scade}</span>}
            </div>
            {n > 0 && (
                <div className="crt-sedute" role="img" aria-label={`${fatte} sedute eseguite su ${n}`}>
                    {Array.from({ length: n }, (_, i) => (
                        <span key={i} className={i < fatte ? 'crt-seduta crt-seduta--fatta' : 'crt-seduta'} />
                    ))}
                </div>
            )}
            <div className="crt-pacchetto-numeri">
                <span><b className="rm-numeri">{fatte}</b> eseguite</span>
                <span><b className="rm-numeri">{restano}</b> da eseguire</span>
            </div>
        </section>
    );
};

const RiquadroAppuntamenti = ({ pazienteId, onApriGiorno }) => {
    /* null finche' non arriva; { errore } se la rotta non risponde. */
    const [dati, setDati] = useState(null);
    useEffect(() => {
        let vivo = true;
        api.get(`/agenda/prossimi/${pazienteId}`)
            .then((r) => { if (vivo) setDati(r.data?.data || { appuntamenti: [], totale: 0 }); })
            .catch(() => { if (vivo) setDati({ errore: true }); });
        return () => { vivo = false; };
    }, [pazienteId]);
    const elenco = dati?.appuntamenti || [];
    return (
        <section className="crt-riquadro" aria-labelledby="crt-titolo-appuntamenti">
            <div className="crt-riquadro-testa">
                <h2 id="crt-titolo-appuntamenti">Prossimi appuntamenti</h2>
                {dati && dati.totale > elenco.length && (
                    <span className="crt-secondario rm-numeri">{elenco.length} di {dati.totale}</span>
                )}
            </div>
            {dati === null ? (
                <p className="crt-vuoto">Caricamento…</p>
            ) : dati.errore ? (
                <p className="crt-vuoto">Non riesco a leggere gli appuntamenti: li trovi in Agenda.</p>
            ) : elenco.length === 0 ? (
                <p className="crt-vuoto">Nessun appuntamento in agenda.</p>
            ) : (
                <ul className="crt-elenco">
                    {elenco.map((a) => (
                        <li key={a.id}>
                            <button type="button" className="crt-appuntamento" data-appuntamento={a.id}
                                    onClick={() => onApriGiorno(a.giorno)}
                                    title="Apri l'agenda su quel giorno">
                                <span className="crt-quando rm-numeri">
                                    {a.di_oggi ? 'OGGI' : dataPunti(a.giorno).slice(0, 5)} {a.ora}
                                </span>
                                <span className="crt-cosa rm-tronca">
                                    {/* Il colore dell'agenda, dai dati (agende.colore). */}
                                    <span className="crt-colore" style={a.agenda_colore ? { background: a.agenda_colore } : undefined} />
                                    {[(a.prestazioni || []).filter(Boolean).join(', ') || 'Appuntamento',
                                      a.stanza_nome || a.agenda_nome].filter(Boolean).join(' · ')}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

const RiepilogoCartella = ({
    pazienteId, visite, anamnesi, pacchetti, onApriVisita, onTutteLeVisite, onAggiungiTerapia, onApriGiorno, children,
}) => (
    /* `sp-recap`: il nome di prima resta sul contenitore del riepilogo. Le
       prove dei percorsi lo aspettano per sapere che la cartella e' aperta
       sulla prima linguetta (G6, G7, G10, H4, H5, provaIndietro). */
    <div className="sp-recap">
        <div className="crt-colonna">
            <RiquadroVisite lettura={visite} onApriVisita={onApriVisita} onTutte={onTutteLeVisite} />
        </div>
        <div className="crt-colonna">
            <RiquadroTerapie lettura={anamnesi} onAggiungi={onAggiungiTerapia} />
            {pacchetti.map((p) => <RiquadroPacchetto key={p.id} pacchetto={p} />)}
            <RiquadroAppuntamenti pazienteId={pazienteId} onApriGiorno={onApriGiorno} />
        </div>
        <div className="crt-intera">{children}</div>
    </div>
);

export default RiepilogoCartella;
